import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type PlayerPosition = 'GK' | 'DEF' | 'MID' | 'FWD';

export interface PlayerStatsDto {
  season: string;
  appearances: number;
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
  minutesPlayed: number;
}

export interface PlayerDto {
  id: string;
  firstName: string;
  lastName: string;
  number: number;
  position: PlayerPosition;
  nationality: string;
  dateOfBirth: string;
  photoUrl: string | null;
  isActive: boolean;
  bio?: unknown;
  height?: number | null;
  weight?: number | null;
  stats: PlayerStatsDto | null;
}

export interface CreatePlayerInput {
  firstName: string;
  lastName: string;
  number: number;
  position: PlayerPosition;
  nationality: string;
  dateOfBirth: string;
  bio?: { it?: string; en?: string };
  height?: number;
  weight?: number;
  photoUrl?: string;
}

export type UpdatePlayerInput = Partial<CreatePlayerInput> & { isActive?: boolean };

const QK = {
  list: (filters: { position?: PlayerPosition; active?: boolean }) =>
    ['players', filters] as const,
  one: (id: string) => ['players', id] as const,
};

export function usePlayers(filters: { position?: PlayerPosition; active?: boolean } = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters.position) params.position = filters.position;
      if (filters.active !== undefined) params.active = String(filters.active);
      const res = await apiClient.get<{ data: PlayerDto[] }>('/teams/own/players', { params });
      return res.data.data;
    },
  });
}

export function usePlayer(id: string | null) {
  return useQuery({
    queryKey: QK.one(id ?? ''),
    enabled: id !== null,
    queryFn: async () => {
      const res = await apiClient.get<PlayerDto>(`/teams/own/players/${id}`);
      return res.data;
    },
  });
}

export function useCreatePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePlayerInput) => {
      const res = await apiClient.post<PlayerDto>('/players', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['players'] }),
  });
}

export function useUpdatePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePlayerInput }) => {
      const res = await apiClient.patch<PlayerDto>(`/players/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['players'] }),
  });
}

export function useDeletePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/players/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['players'] }),
  });
}
